import instance from "../axios";

export type QuestionnaireTemplate = {
  id: number;
  name: string;
  schema: string;
  public_token?: string | null;
  created_at: string;
  updated_at: string;
};

export type QuestionnaireResponse = {
  id: string;
  questionnaire_id: number;
  share_token: string;
  shareUrl: string;
  client_name: string | null;
  client_email: string | null;
  internal_note: string | null;
  status: "DRAFT" | "SUBMITTED" | "CONVERTED" | "REJECTED";
  schema_snapshot: string;
  answers: string;
  enrichment: string | null;
  telemetry_trace: string | null;
  expires_at: string | null;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
  delegated_to_user_id?: number | null;
  delegated_user_ids?: string | null;
  creator?: {
    display_name: string;
    email: string;
  };
  // ── NDA fields ─────────────────────────────────────────────────────────
  nda_required?: boolean;
  nda_status?: "PENDING" | "SIGNED";
  nda_enabled_after_creation?: boolean;
  nda_signed_at?: string | null;
  nda_signer_name?: string | null;
};

// NDA-specific status shape returned by GET /intake/:token/nda
export type NdaStatusResponse = {
  nda_required: boolean;
  nda_status: "PENDING" | "SIGNED";
  nda_enabled_after_creation: boolean;
  nda_signed_at: string | null;
  nda_signer_name: string | null;
  nda_text: string | null;
};

export const getTemplates = async () => {
  const res = await instance.get<QuestionnaireTemplate[]>("/questionnaires/templates");
  return res.data;
};

export const saveTemplate = async (data: { id?: number; name: string; schema: any }) => {
  const res = await instance.post<QuestionnaireTemplate>("/questionnaires/templates", data);
  return res.data;
};

export const generateResponseLink = async (data: {
  templateId?: number;
  clientName?: string;
  clientEmail?: string;
  internalNote?: string;
  customSchemaSnapshot?: any;
  expiresDays?: number;
  requireNda?: boolean; // ── NEW: optional NDA gate
}) => {
  const res = await instance.post<QuestionnaireResponse & { shareUrl: string }>(
    "/questionnaires/responses",
    data
  );
  return res.data;
};

export const getResponses = async () => {
  const res = await instance.get<QuestionnaireResponse[]>("/questionnaires/responses");
  return res.data;
};

export const getResponseById = async (id: string) => {
  const res = await instance.get<any>(`/questionnaires/responses/${id}`);
  return res.data;
};

export const updatePricingOverrides = async (
  id: string,
  data: {
    region: string;
    competitiveMode: boolean;
    continuousACV: boolean;
    manualCoefficient: number;
    manualOverridePrice: number | null;
  }
) => {
  const res = await instance.post<any>(`/questionnaires/responses/${id}/overrides`, data);
  return res.data;
};

export const convertToProgram = async (id: string) => {
  const res = await instance.post<any>(`/questionnaires/responses/${id}/convert`);
  return res.data;
};

export const rejectResponse = async (id: string) => {
  const res = await instance.post<any>(`/questionnaires/responses/${id}/reject`);
  return res.data;
};

export const markAsLost = async (id: string, lostReason: string, lostNote?: string) => {
  const res = await instance.post<any>(`/questionnaires/responses/${id}/lost`, { lostReason, lostNote });
  return res.data;
};

export const extendResponseExpiry = async (id: string, days?: number, newExpiryDate?: string) => {
  const res = await instance.post<any>(`/questionnaires/responses/${id}/extend`, { days, newExpiryDate });
  return res.data;
};

export const getClientScopingStatus = async () => {
  const res = await instance.get<{
    locked: boolean;
    status: string;
    shareToken: string | null;
    activeDraftToken?: string | null;
    activeDraftStatus?: string | null;
  }>("/questionnaires/client-status");
  return res.data;
};

export const getOrgResponses = async () => {
  const res = await instance.get<QuestionnaireResponse[]>("/questionnaires/organization");
  return res.data;
};

export const delegateResponse = async (id: string, delegatedUserIds: number[]) => {
  const res = await instance.post<{ message: string; delegated_user_ids: number[] }>(
    `/questionnaires/responses/${id}/delegate`,
    { delegatedUserIds }
  );
  return res.data;
};

/**
 * Returns the org sub-users for the authenticated business owner/admin.
 * Used to populate the delegation roster in History of Scopes.
 * Hits GET /api/v2/rbac/sub-users — more reliable than parsing invitation records.
 */
export const getOrgTeamMembers = async (): Promise<
  { user_id: number; username: string; email: string; role: number; display_name?: string }[]
> => {
  const res = await instance.get("/v2/rbac/sub-users");
  return res.data;
};


// ==================== PUBLIC LINKS ====================
export const getPublicIntake = async (token: string) => {
  const res = await instance.get<any>(`/intake/${token}`);
  return res.data;
};

export const savePublicDraft = async (
  token: string,
  data: {
    answers: any;
    clientName?: string;
    clientEmail?: string;
  }
) => {
  const res = await instance.patch<any>(`/intake/${token}`, data);
  return res.data;
};

export const submitPublicIntake = async (
  token: string,
  data: {
    answers: any;
    telemetryTrace: any;
  }
) => {
  const res = await instance.post<any>(`/intake/${token}/submit`, data);
  return res.data;
};

export const getPublicTemplate = async (publicToken: string) => {
  const res = await instance.get<any>(`/intake/templates/${publicToken}`);
  return res.data;
};

export const createPublicTemplateDraft = async (
  publicToken: string,
  data: {
    answers: any;
  }
) => {
  const res = await instance.post<any>(`/intake/templates/${publicToken}/draft`, data);
  return res.data;
};

export const submitPublicTemplate = async (
  publicToken: string,
  data: {
    answers: any;
    telemetryTrace: any;
  }
) => {
  const res = await instance.post<any>(`/intake/templates/${publicToken}/submit`, data);
  return res.data;
};

// ═══════════════════════════════════════════════════════════════════════════════
// NDA API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * GET /intake/:token/nda
 * Returns NDA status, signerName, and full NDA document text.
 */
export const getNdaStatus = async (token: string): Promise<NdaStatusResponse> => {
  const res = await instance.get<NdaStatusResponse>(`/intake/${token}/nda`);
  return res.data;
};

/**
 * POST /intake/:token/nda-sign
 * Submits the client's drawn signature and legal name to sign the NDA.
 */
export const signNda = async (
  token: string,
  data: { signerName: string; signerAddress: string; signatureBase64: string; clientName?: string }
): Promise<{ success: boolean; nda_signed_at: string; nda_signer_name: string; nda_status: string }> => {
  const res = await instance.post(`/intake/${token}/nda-sign`, data);
  return res.data;
};

/**
 * PATCH /questionnaires/responses/:id/nda-toggle
 * Allows a Solutions Specialist to enable/disable NDA for a DRAFT session.
 */
export const toggleNdaRequired = async (
  id: string,
  requireNda: boolean
): Promise<{ message: string; nda_required: boolean; nda_status: string; nda_enabled_after_creation: boolean }> => {
  const res = await instance.patch(`/questionnaires/responses/${id}/nda-toggle`, { requireNda });
  return res.data;
};

/**
 * POST /questionnaires/responses/:id/nda-sign-ss
 * Allows a Solutions Specialist to counter-sign an NDA that the client has signed (CLIENT_SIGNED → SIGNED).
 */
export const signNdaAsSs = async (
  id: string,
  data: { ssSignerName: string; ssSignatureBase64: string }
): Promise<{
  success: boolean;
  nda_status: string;
  nda_ss_signer_name: string;
  nda_ss_signed_at: string;
  nda_pdf_path: string | null;
  download_url: string;
}> => {
  const res = await instance.post(`/questionnaires/responses/${id}/nda-sign-ss`, data);
  return res.data;
};
